import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { isLocalhostSignIn } from '@/config/aws'
import AppLayout from '@/components/layout/AppLayout'
import CallbackPage from '@/pages/auth/CallbackPage'
import HomePage from '@/pages/HomePage'
import LoadingSpinner from '@/components/common/LoadingSpinner'

const APP_ID = 'playback-editor'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { user, isAuthenticated, isLoading, signIn } = useAuth()
  const autoSignInTriggered = useRef(false)

  useEffect(() => {
    if (isLoading || isAuthenticated || autoSignInTriggered.current) return
    autoSignInTriggered.current = true
    if (isLocalhostSignIn) {
      signIn()
    } else {
      const portalUrl = import.meta.env.VITE_PORTAL_URL
      if (portalUrl) {
        const returnTo = encodeURIComponent(window.location.href)
        window.location.href = `${portalUrl}/?return_to=${returnTo}`
      } else {
        signIn()
      }
    }
  }, [isLoading, isAuthenticated, signIn])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <LoadingSpinner size="lg" />
        <p className="ml-4 text-gray-500">{t('app.redirecting')}</p>
      </div>
    )
  }

  const allowedApps = user?.allowedApps ?? []
  const hasAccess =
    user?.role === 'admin' ||
    allowedApps.includes('*') ||
    allowedApps.includes(APP_ID)

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 gap-4">
        <p className="text-lg font-semibold text-gray-700">
          {t('app.noAccess')}
        </p>
        <button
          onClick={() => signIn()}
          className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          {t('app.switchAccount')}
        </button>
      </div>
    )
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
